import React, { useEffect, useMemo, useRef, useState } from "react";
import { BiDownload, BiPlus, BiRefresh } from "react-icons/bi";
import { BsColumnsGap, BsFillFunnelFill, BsGrid3X3Gap, BsSliders2 } from "react-icons/bs";
import { FaThList } from "react-icons/fa";
import TableLayout from "./Table";
import GridLayout from "./Grid";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar";
import ActiveFilters from "./ActiveFilters";
import ErrorState from "./ErrorState";
import Filter from "./Filter";
import { unmatchedSearchKeys } from "./columnFilters";

const ListLayout = ({
    title,
    subTitle,
    description,
    data = {},
    loading = false,
    error,
    onRetry,
    onRefresh,
    table = [],
    grid,
    defaultView = "Table",
    page = 1,
    setPageNo,
    pageSize,
    onPageSizeChange,
    showPage = false,
    filters = {},
    initialFilters = {},
    filterItems = {},
    onFilterChange,
    resetFilter,
    searchKeys,
    searchPlaceholder,
    onRowClick = false,
    addList,
    download,
    toolbarExtra,
}) => {
    const [view, setView] = useState(defaultView);
    const [visibleColumns, setVisibleColumns] = useState(() => table.map((col) => col.key));
    const [refreshing, setRefreshing] = useState(false);
    const hadDataRef = useRef(false);

    const list = data?.list || [];
    const total = data?.total;

    const handleColumnToggle = (columnKey) => {
        setVisibleColumns((prev) =>
            prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey]
        );
    };
    const activeColumns = useMemo(
        () => table.filter((col) => col.mandatory || visibleColumns.includes(col.key)),
        [table, visibleColumns]
    );

    // Table view moves any searchKeys entry that matches a visible column
    // into that column's own header (see columnFilters.js) - only entries
    // that don't match anything still need the toolbar search box, so it
    // never disappears and never duplicates a search that's already live
    // in a header. Grid view is untouched: it always gets the full list.
    const tableToolbarSearchKeys = useMemo(
        () => unmatchedSearchKeys(activeColumns, searchKeys),
        [activeColumns, searchKeys]
    );
    const toolbarSearchKeys = view === "Table" ? tableToolbarSearchKeys : searchKeys;

    useEffect(() => {
        if (!loading && list.length) hadDataRef.current = true;
    }, [loading, list.length]);
    const isRefreshing = loading && hadDataRef.current;

    const emptyReason = useMemo(() => {
        if (filters?.search) return "search";
        const hasActiveFilter = Object.keys(initialFilters || {}).some((key) => {
            if (["search", "searchKey", "count", "sortBy", "sort"].includes(key)) return false;
            return filters?.[key] !== undefined && filters[key] !== initialFilters[key];
        });
        return hasActiveFilter ? "filter" : "empty";
    }, [filters, initialFilters]);

    const handleRefresh = async () => {
        if (refreshing || !onRefresh) return;
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    };

    const clearSearch = () => {
        onFilterChange?.("search", "");
        onFilterChange?.("searchKey", searchKeys?.[0]?.key);
    };

    const rowsPerPage = filters?.count || pageSize || 10;

    return (
        <>
            <div className="row g-4">
                <div className="col-12">
                    <div className="content-area">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">

                            <div className="list-header">
                                <div>
                                    <h4 className="list-header-title">{title}{subTitle ? ` - ${subTitle}` : ""}</h4>
                                    {description ? <p className="list-header-desc">{description}</p> : ""}
                                </div>
                                <div className="list-header-actions">
                                    {typeof total === "number" ? <span className="list-header-count">{total.toLocaleString()} records</span> : ""}
                                    {download ? (
                                        <button className="btn btn-outline-secondary" onClick={download} disabled={loading}>
                                            <BiDownload className="me-1" /> Export
                                        </button>
                                    ) : ""}
                                    {addList ? (
                                        <button className="btn btn-primary" onClick={addList}>
                                            <BiPlus className="me-1" /> Add {title}
                                        </button>
                                    ) : ""}
                                </div>
                            </div>

                            <div className="list-toolbar">
                                <div className="list-toolbar-left">
                                    {toolbarSearchKeys?.length ? (
                                        <SearchBar
                                            filters={filters}
                                            title={title}
                                            placeholder={searchPlaceholder}
                                            searchKeys={toolbarSearchKeys}
                                            onFilterChange={onFilterChange}
                                        />
                                    ) : ""}
                                    {toolbarExtra}
                                </div>

                                <div className="list-toolbar-right">
                                    {onRefresh ? (
                                        <button
                                            type="button"
                                            className="btn list-icon-btn"
                                            onClick={handleRefresh}
                                            disabled={refreshing || loading}
                                            aria-label="Refresh list"
                                        >
                                            <BiRefresh className={refreshing ? "spin" : ""} />
                                        </button>
                                    ) : ""}

                                    {view === "Table" && table.filter((c) => !c.mandatory)?.length ? (
                                        <div className="dropdown">
                                            <button
                                                className="btn list-icon-btn dropdown-toggle"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                data-bs-auto-close="outside"
                                                aria-label="Column settings"
                                            >
                                                <BsColumnsGap />
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0 p-2" style={{ position: "relative", zIndex: 1060 }}>
                                                {table.filter((c) => !c.mandatory).map((col) => (
                                                    <li key={col.key} className="py-1">
                                                        <div className="form-check mx-2">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`col-${col.key}`}
                                                                checked={visibleColumns.includes(col.key)}
                                                                onChange={() => handleColumnToggle(col.key)}
                                                            />
                                                            <label className="form-check-label small" htmlFor={`col-${col.key}`}> {col.name} </label>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : ""}

                                    {/* Table view moves filtering into the column headers themselves
                                        (Table.js -> ColumnHeaderControls) - this offcanvas trigger, and the
                                        offcanvas panel it opens below, stay exclusively a Grid-view affordance
                                        so Grid's filtering is completely unchanged. */}
                                    {view === "Grid" && Object.keys(filterItems || {}).length ? (
                                        <button className="btn list-filter-trigger" type="button" data-bs-toggle="offcanvas" data-bs-target="#filterSidebar">
                                            <BsFillFunnelFill /> Filters
                                        </button>
                                    ) : ""}

                                    {table?.length && grid ? (
                                        <div className="list-view-toggle" role="group" aria-label="Switch between table and grid view">
                                            <button type="button" className={view === "Table" ? "active" : ""} onClick={() => setView("Table")} aria-pressed={view === "Table"} aria-label="Table view">
                                                <FaThList />
                                            </button>
                                            <button type="button" className={view === "Grid" ? "active" : ""} onClick={() => setView("Grid")} aria-pressed={view === "Grid"} aria-label="Grid view">
                                                <BsGrid3X3Gap />
                                            </button>
                                        </div>
                                    ) : ""}
                                </div>
                            </div>

                            <ActiveFilters
                                filters={filters}
                                initialFilters={initialFilters}
                                filterItems={filterItems}
                                onFilterChange={onFilterChange}
                                resetFilter={resetFilter}
                            />

                            <div className="card-body p-0">
                                {error ? (
                                    <ErrorState message={typeof error === "string" ? error : undefined} onRetry={onRetry} />
                                ) : view === "Table" ? (
                                    <TableLayout
                                        columns={activeColumns}
                                        data={list}
                                        loading={loading}
                                        isRefreshing={isRefreshing}
                                        page={page}
                                        pageSize={rowsPerPage}
                                        onRowClick={onRowClick}
                                        emptyReason={emptyReason}
                                        addLabel={addList ? `Add ${title}` : undefined}
                                        onAddNew={addList}
                                        onClearSearch={clearSearch}
                                        onClearFilters={resetFilter}
                                        filterItems={filterItems}
                                        searchKeys={searchKeys}
                                        filters={filters}
                                        initialFilters={initialFilters}
                                        onFilterChange={onFilterChange}
                                    />
                                ) : (
                                    <div className="p-4">
                                        <GridLayout
                                            data={list}
                                            loading={loading}
                                            isRefreshing={isRefreshing}
                                            gridLayout={grid}
                                            emptyReason={emptyReason}
                                            addLabel={addList ? `Add ${title}` : undefined}
                                            onAddNew={addList}
                                            onClearSearch={clearSearch}
                                            onClearFilters={resetFilter}
                                        />
                                    </div>
                                )}
                            </div>

                            {!showPage && !error ? (
                                <div className="card-footer bg-white">
                                    <Pagination
                                        current={Number(page)}
                                        total={total}
                                        pageSize={rowsPerPage}
                                        onPageChange={setPageNo}
                                        onPageSizeChange={onPageSizeChange}
                                        disabled={loading}
                                    />
                                </div>
                            ) : ""}
                        </div>
                    </div>
                </div>
            </div>

            <div className="offcanvas offcanvas-end border-0 shadow list-filter-offcanvas" tabIndex="-1" id="filterSidebar" aria-labelledby="filterSidebarLabel">
                <div className="offcanvas-header border-bottom">
                    <h5 className="offcanvas-title fw-bold" id="filterSidebarLabel">
                        <BsSliders2 className="me-2" /> Filters
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                </div>
                <div className="offcanvas-body">
                    <Filter
                        initialFilters={initialFilters}
                        currentFilters={filters}
                        filterItems={filterItems}
                        onChange={onFilterChange}
                        resetFilter={resetFilter}
                        onClose={() => document.getElementById("filterSidebar")?.querySelector(".btn-close")?.click()}
                    />
                </div>
            </div>
        </>
    );
};

export default ListLayout;
